import React, { useId, useMemo, useState } from 'react';

/*
 * Small inline-SVG charts for the Monitoring panel, written against the
 * data-viz rules: one axis per chart, 2px lines, >= 8px hover markers,
 * recessive grid, a legend only from two series, text in text tokens,
 * series colours from CSS variables (--viz-series-1/2, set per theme in
 * DevicesDetailsMonitoring.css), and a hover tooltip on every form.
 *
 * The x axis is always the civil day, 00:00 -> 24:00, so two charts of the
 * same day line up and a gap in the data shows as a gap.
 */

export interface Series {
  id: string;
  label: string;
  /** Points as [minute of day 0..1440, value]; sorted ascending by minute. */
  points: Array<[number, number]>;
}

export const MINUTES_IN_DAY = 1440;

/** Minute of the day of an ISO timestamp, in the browser's zone (what the time column shows). */
export const minuteOfDay = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60;
};

/** Counts per hour of the day, from timestamps. */
export const countsPerHour = (timestamps: Array<string | null | undefined>): number[] => {
  const hours = new Array<number>(24).fill(0);
  for (const iso of timestamps) {
    const m = minuteOfDay(iso);
    if (m !== null) hours[Math.min(23, Math.floor(m / 60))] += 1;
  }
  return hours;
};

/** Points of a numeric field over the day, oldest first, nulls skipped. */
export const seriesOf = <T,>(items: T[], at: (i: T) => string | null, value: (i: T) => number | null | undefined): Array<[number, number]> => {
  const out: Array<[number, number]> = [];
  for (const item of items) {
    const m = minuteOfDay(at(item));
    const v = value(item);
    if (m !== null && typeof v === 'number' && Number.isFinite(v)) out.push([m, v]);
  }
  return out.sort((a, b) => a[0] - b[0]);
};

const hhmm = (minute: number) => `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(Math.floor(minute % 60)).padStart(2, '0')}`;

/** A tidy value axis: 4 ticks from a rounded min to a rounded max; `integer` for counts (no 0.2 tick). */
const niceScale = (values: number[], zeroBased = false, integer = false) => {
  if (values.length === 0) return { min: 0, max: 1, ticks: [0, 1] };
  let min = zeroBased ? 0 : Math.min(...values);
  let max = Math.max(...values);
  if (max === min) { max = min + 1; if (!zeroBased) min -= 1; }
  const span = max - min;
  const step = Math.max(integer ? 1 : 0, Math.pow(10, Math.floor(Math.log10(span / 3))));
  const unit = [1, 2, 5, 10].map((k) => k * step).find((u) => span / u <= 5) ?? step * 10;
  min = Math.floor(min / unit) * unit;
  max = Math.ceil(max / unit) * unit;
  const ticks: number[] = [];
  for (let v = min; v <= max + unit / 2; v += unit) ticks.push(Number(v.toFixed(6)));
  return { min, max, ticks };
};

const W = 640;
const H = 180;
const PAD = { top: 10, right: 12, bottom: 24, left: 44 };

interface LineChartProps {
  title: string;
  unit?: string;
  series: Series[];
  format?: (v: number) => string;
  zeroBased?: boolean;
  emptyLabel: string;
}

/** Values over the day; one or two series, hover crosshair with a tooltip. */
export const LineChart: React.FC<LineChartProps> = ({ title, unit, series, format = (v) => String(v), zeroBased, emptyLabel }) => {
  const id = useId();
  const [hoverMinute, setHoverMinute] = useState<number | null>(null);
  const all = series.flatMap((s) => s.points.map((p) => p[1]));
  const scale = useMemo(() => niceScale(all, zeroBased), [all.join(','), zeroBased]); // eslint-disable-line react-hooks/exhaustive-deps
  const x = (minute: number) => PAD.left + (minute / MINUTES_IN_DAY) * (W - PAD.left - PAD.right);
  const y = (v: number) => PAD.top + (1 - (v - scale.min) / (scale.max - scale.min)) * (H - PAD.top - PAD.bottom);

  // The hovered minute snaps to the nearest point of each series.
  const hovered = hoverMinute === null ? null : series.map((s) => {
    let best: [number, number] | null = null;
    for (const p of s.points) if (best === null || Math.abs(p[0] - hoverMinute) < Math.abs(best[0] - hoverMinute)) best = p;
    return best;
  });
  const anchor = hovered?.find((p) => p !== null) ?? null;

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * W;
    const minute = ((px - PAD.left) / (W - PAD.left - PAD.right)) * MINUTES_IN_DAY;
    setHoverMinute(Math.max(0, Math.min(MINUTES_IN_DAY, minute)));
  };

  return (
    <figure className="viz" data-testid="viz-line">
      <figcaption className="viz-title">
        <span>{title}{unit ? <span className="viz-unit"> ({unit})</span> : null}</span>
        {series.length > 1 && (
          <span className="viz-legend" aria-label="legend">
            {series.map((s, i) => (
              <span key={s.id} className="viz-legend-item"><i className={`viz-swatch viz-series-${i + 1}`} />{s.label}</span>
            ))}
          </span>
        )}
      </figcaption>
      {all.length === 0 ? (
        <div className="viz-empty">{emptyLabel}</div>
      ) : (
        <svg viewBox={`0 0 ${W} ${H}`} className="viz-svg" role="img" aria-labelledby={`${id}-t`} onMouseMove={onMove} onMouseLeave={() => setHoverMinute(null)}>
          <title id={`${id}-t`}>{title}</title>
          {scale.ticks.map((tick) => (
            <g key={tick}>
              <line className="viz-grid" x1={PAD.left} x2={W - PAD.right} y1={y(tick)} y2={y(tick)} />
              <text className="viz-axis" x={PAD.left - 6} y={y(tick)} dy="0.35em" textAnchor="end">{format(tick)}</text>
            </g>
          ))}
          {[0, 6, 12, 18, 24].map((h) => (
            <text key={h} className="viz-axis" x={x(h * 60)} y={H - 6} textAnchor={h === 0 ? 'start' : h === 24 ? 'end' : 'middle'}>{`${String(h).padStart(2, '0')}:00`}</text>
          ))}
          {series.map((s, i) => (
            <path
              key={s.id}
              className={`viz-line viz-series-${i + 1}`}
              d={s.points.map((p, k) => `${k === 0 ? 'M' : 'L'}${x(p[0]).toFixed(1)},${y(p[1]).toFixed(1)}`).join(' ')}
            />
          ))}
          {anchor && (
            <g className="viz-hover">
              <line className="viz-crosshair" x1={x(anchor[0])} x2={x(anchor[0])} y1={PAD.top} y2={H - PAD.bottom} />
              {hovered!.map((p, i) => p && <circle key={series[i].id} className={`viz-marker viz-series-${i + 1}`} cx={x(p[0])} cy={y(p[1])} r={4} />)}
            </g>
          )}
        </svg>
      )}
      {anchor && (
        <div className="viz-tooltip" role="status">
          <span className="viz-tooltip-time">{hhmm(anchor[0])}</span>
          {hovered!.map((p, i) => p && (
            <span key={series[i].id} className="viz-tooltip-row"><i className={`viz-swatch viz-series-${i + 1}`} />{series[i].label}: <strong>{format(p[1])}{unit ? ` ${unit}` : ''}</strong></span>
          ))}
        </div>
      )}
    </figure>
  );
};

interface BarChartProps {
  title: string;
  /** 24 values, one per hour. */
  perHour: number[];
  valueLabel: string;
  emptyLabel: string;
}

/** Counts per hour of the day; one series, hover tooltip per bar. */
export const BarChart: React.FC<BarChartProps> = ({ title, perHour, valueLabel, emptyLabel }) => {
  const id = useId();
  const [hover, setHover] = useState<number | null>(null);
  const total = perHour.reduce((a, b) => a + b, 0);
  const scale = useMemo(() => niceScale(perHour, true, true), [perHour.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  const plotW = W - PAD.left - PAD.right;
  const slot = plotW / 24;
  const y = (v: number) => PAD.top + (1 - v / scale.max) * (H - PAD.top - PAD.bottom);
  const base = y(0);

  return (
    <figure className="viz" data-testid="viz-bar">
      <figcaption className="viz-title"><span>{title}</span></figcaption>
      {total === 0 ? (
        <div className="viz-empty">{emptyLabel}</div>
      ) : (
        <svg viewBox={`0 0 ${W} ${H}`} className="viz-svg" role="img" aria-labelledby={`${id}-t`} onMouseLeave={() => setHover(null)}>
          <title id={`${id}-t`}>{title}</title>
          {scale.ticks.map((tick) => (
            <g key={tick}>
              <line className="viz-grid" x1={PAD.left} x2={W - PAD.right} y1={y(tick)} y2={y(tick)} />
              <text className="viz-axis" x={PAD.left - 6} y={y(tick)} dy="0.35em" textAnchor="end">{tick}</text>
            </g>
          ))}
          {perHour.map((v, h) => {
            const left = PAD.left + h * slot + 2;
            const width = Math.max(2, slot - 4);
            const top = v > 0 ? y(v) : base;
            return (
              <g key={h} onMouseEnter={() => setHover(h)}>
                {/* hit target wider than the bar */}
                <rect x={PAD.left + h * slot} y={PAD.top} width={slot} height={H - PAD.top - PAD.bottom} fill="transparent" />
                {v > 0 && <rect className={`viz-bar viz-series-1${hover === h ? ' is-hover' : ''}`} x={left} y={top} width={width} height={Math.max(1, base - top)} rx={3} />}
              </g>
            );
          })}
          <line className="viz-baseline" x1={PAD.left} x2={W - PAD.right} y1={base} y2={base} />
          {[0, 6, 12, 18, 24].map((h) => (
            <text key={h} className="viz-axis" x={PAD.left + h * slot} y={H - 6} textAnchor={h === 0 ? 'start' : h === 24 ? 'end' : 'middle'}>{`${String(h).padStart(2, '0')}:00`}</text>
          ))}
        </svg>
      )}
      {hover !== null && total > 0 && (
        <div className="viz-tooltip" role="status">
          <span className="viz-tooltip-time">{`${String(hover).padStart(2, '0')}:00 – ${String(hover + 1).padStart(2, '0')}:00`}</span>
          <span className="viz-tooltip-row"><i className="viz-swatch viz-series-1" />{valueLabel}: <strong>{perHour[hover]}</strong></span>
        </div>
      )}
    </figure>
  );
};

export interface Category {
  label: string;
  /** One value per series (two at most), stacked. */
  values: number[];
  /** The tooltip's first line (defaults to the label). */
  tooltipTitle?: string;
}

interface CategoryBarChartProps {
  title: string;
  categories: Category[];
  seriesLabels: string[];
  emptyLabel: string;
  /** Index of the selected category, when the bars are clickable. */
  selected?: number | null;
  onSelect?: (index: number) => void;
}

/**
 * One stacked bar per category (here: per call of the day), two series at
 * most, a 2px surface gap between segments, a legend from two series, hover
 * tooltip per bar, and a click to select one. Labels thin out when the bars
 * get narrow.
 */
export const CategoryBarChart: React.FC<CategoryBarChartProps> = ({ title, categories, seriesLabels, emptyLabel, selected = null, onSelect }) => {
  const id = useId();
  const [hover, setHover] = useState<number | null>(null);
  const totals = categories.map((c) => c.values.reduce((a, b) => a + b, 0));
  const scale = useMemo(() => niceScale(totals, true, true), [totals.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  const plotW = W - PAD.left - PAD.right;
  const n = Math.max(1, categories.length);
  const slot = plotW / n;
  const y = (v: number) => PAD.top + (1 - v / scale.max) * (H - PAD.top - PAD.bottom);
  const base = y(0);
  const everyNth = Math.max(1, Math.ceil(n / 12));
  const empty = totals.every((v) => v === 0);

  return (
    <figure className={`viz${onSelect ? ' is-clickable' : ''}`} data-testid="viz-category-bar">
      <figcaption className="viz-title">
        <span>{title}</span>
        {seriesLabels.length > 1 && (
          <span className="viz-legend" aria-label="legend">
            {seriesLabels.map((label, i) => (
              <span key={label} className="viz-legend-item"><i className={`viz-swatch viz-series-${i + 1}`} />{label}</span>
            ))}
          </span>
        )}
      </figcaption>
      {empty ? (
        <div className="viz-empty">{emptyLabel}</div>
      ) : (
        <svg viewBox={`0 0 ${W} ${H}`} className="viz-svg" role="img" aria-labelledby={`${id}-t`} onMouseLeave={() => setHover(null)}>
          <title id={`${id}-t`}>{title}</title>
          {scale.ticks.map((tick) => (
            <g key={tick}>
              <line className="viz-grid" x1={PAD.left} x2={W - PAD.right} y1={y(tick)} y2={y(tick)} />
              <text className="viz-axis" x={PAD.left - 6} y={y(tick)} dy="0.35em" textAnchor="end">{tick}</text>
            </g>
          ))}
          {categories.map((c, k) => {
            const left = PAD.left + k * slot + Math.min(4, slot * 0.15);
            const width = Math.max(2, slot - Math.min(8, slot * 0.3));
            let acc = 0;
            return (
              <g key={k} onMouseEnter={() => setHover(k)} onClick={onSelect ? () => onSelect(k) : undefined} data-testid={`viz-category-${k}`}>
                <rect x={PAD.left + k * slot} y={PAD.top} width={slot} height={H - PAD.top - PAD.bottom} fill="transparent" />
                {selected === k && <rect className="viz-selection" x={PAD.left + k * slot} y={PAD.top} width={slot} height={H - PAD.top - PAD.bottom} rx={3} />}
                {c.values.map((v, i) => {
                  if (v <= 0) return null;
                  const top = y(acc + v);
                  const bottom = y(acc) - (acc > 0 ? 2 : 0);
                  acc += v;
                  return <rect key={i} className={`viz-bar viz-series-${i + 1}${hover === k ? ' is-hover' : ''}${selected !== null && selected !== k ? ' is-dim' : ''}`} x={left} y={top} width={width} height={Math.max(1, bottom - top)} rx={2} />;
                })}
                {k % everyNth === 0 && (
                  <text className="viz-axis" x={PAD.left + k * slot + slot / 2} y={H - 6} textAnchor="middle">{c.label}</text>
                )}
              </g>
            );
          })}
          <line className="viz-baseline" x1={PAD.left} x2={W - PAD.right} y1={base} y2={base} />
        </svg>
      )}
      {hover !== null && !empty && categories[hover] && (
        <div className="viz-tooltip" role="status">
          <span className="viz-tooltip-time">{categories[hover].tooltipTitle ?? categories[hover].label}</span>
          {categories[hover].values.map((v, i) => (
            <span key={i} className="viz-tooltip-row"><i className={`viz-swatch viz-series-${i + 1}`} />{seriesLabels[i]}: <strong>{v}</strong></span>
          ))}
        </div>
      )}
    </figure>
  );
};
